import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Star } from "lucide-react";
import type { DailyJournal } from "@/types";

interface JournalCalendarProps {
  journals: DailyJournal[];
}

/**
 * Calendar view for browsing journals by date
 *
 * @param journals - List of journals to display
 * @returns Interactive calendar with journal indicators
 */
export function JournalCalendar({ journals }: JournalCalendarProps) {
  const [currentDate, setCurrentDate] = useState(new Date());

  // Create a map of dates to journals for quick lookup
  const journalsByDate = new Map(
    journals.map((j) => [j.date, j])
  );

  // Get calendar data
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startingDay = firstDay.getDay();
  const daysInMonth = lastDay.getDate();

  // Generate calendar grid
  const days: (number | null)[] = [];
  for (let i = 0; i < startingDay; i++) {
    days.push(null);
  }
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(i);
  }

  // Navigation handlers
  const prevMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };

  // Format date string for lookup
  const formatDateKey = (day: number) => {
    return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  };

  // Weekday headers
  const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return (
    <div className="bg-white rounded-2xl border p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={prevMonth}
          className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <ChevronLeft size={20} />
        </button>

        <h2 className="text-xl font-display font-semibold text-primary-950">
          {currentDate.toLocaleDateString("ja-JP", {
            year: "numeric",
            month: "long",
          })}
        </h2>

        <button
          onClick={nextMonth}
          className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <ChevronRight size={20} />
        </button>
      </div>

      {/* Weekday headers */}
      <div className="grid grid-cols-7 gap-2 mb-2">
        {weekdays.map((day) => (
          <div
            key={day}
            className="text-center text-sm font-medium text-slate-500 py-2"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-2">
        {days.map((day, index) => {
          if (day === null) {
            return <div key={`empty-${index}`} className="aspect-square" />;
          }

          const dateKey = formatDateKey(day);
          const journal = journalsByDate.get(dateKey);
          const isToday =
            day === new Date().getDate() &&
            month === new Date().getMonth() &&
            year === new Date().getFullYear();

          return journal ? (
            <Link
              key={day}
              to={`/journals/${dateKey}`}
              className={`
                aspect-square rounded-xl flex flex-col items-center justify-center
                transition-all duration-200 hover:scale-105
                ${
                  isToday
                    ? "bg-primary-600 text-white shadow-lg shadow-primary-600/25"
                    : "bg-primary-100 text-primary-900 hover:bg-primary-200"
                }
              `}
            >
              <span className="font-medium">{day}</span>
              <div className="flex gap-0.5 mt-1">
                {Array.from({ length: Math.min(journal.importanceScore, 3) }).map(
                  (_, i) => (
                    <Star
                      key={i}
                      size={8}
                      className={isToday ? "fill-white" : "fill-primary-600"}
                    />
                  )
                )}
              </div>
            </Link>
          ) : (
            <div
              key={day}
              className={`
                aspect-square rounded-xl flex items-center justify-center
                ${
                  isToday
                    ? "bg-slate-200 font-medium"
                    : "bg-slate-50 text-slate-400"
                }
              `}
            >
              {day}
            </div>
          );
        })}
      </div>
    </div>
  );
}

